import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';
import { FlyoutMenu } from '../flyout-menu/FlyoutMenu';
import { EnvVarsFeaturesContext } from '../../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../../scaffold/UserContext';
import styles from './DesktopMenu.scss';
import { isTouchDevice } from '../../../utils/device/identifyDevice';
import { DesktopMenuItem } from '../desktop-menu-item/DesktopMenuItem';
import { filterMenuData } from '../../../utils/menu/filterMenuData';
import { formatTestId } from '../../../utils/test-id/format-testid';
import { DESKTOP_MI, NAV_EXP_PARAMS_PROP } from '../../../utils/constants';
import { getExperiencesWithMatchingParamValues } from '../../../utils/monetate/getMonetateParams';

export const DesktopMenu = ({ child }) => {
  const {
    envVars: { monetate },
    features: { monetateExperiences },
  } = useContext(EnvVarsFeaturesContext);
  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext);

  const [activeIndex, setActiveIndex] = useState(-1);

  const MAX_NAVIGATION_ITEMS = 12;

  const filteredChild = filterMenuData(child, ['FLYOUT']).slice(
    0,
    MAX_NAVIGATION_ITEMS
  );

  const onSelect = index => () => {
    setActiveIndex(index);

    if (activeIndex !== index) {
      const categoryName = formatTestId(filteredChild?.[index]?.name);
      if (categoryName) {
        const matchingExperiences = getExperiencesWithMatchingParamValues(
          { monetateExperiences },
          NAV_EXP_PARAMS_PROP,
          categoryName
        );

        if (matchingExperiences.length > 0) {
          matchingExperiences.forEach(exp => {
            recordImpressionByName(
              monetate.channel,
              matchingExperiences,
              exp.experienceName,
              monetateId
            );
          });
        }
      }

      recordImpressionByName(
        monetate.channel,
        monetateExperiences,
        DESKTOP_MI,
        monetateId
      );
    }
  };

  const onClose = () => {
    if (activeIndex >= 0) setActiveIndex(-1);
  };

  const overlayClassName = classNames(styles.desktopMenuOverlay, {
    [styles.desktopMenuOverlay__active]: activeIndex !== -1,
  });

  const isTouch = typeof window !== 'undefined' && isTouchDevice();

  return (
    <nav className={styles.desktopMenu} data-testid="navigation-menu">
      <div className={styles.pageContainer}>
        <div className={styles.grid}>
          <ul
            className={styles.desktopMenuList}
            data-testid="navigation-menu-list"
          >
            {filteredChild.map((item, index) => {
              const { contentId, name, url, child: childItems } = item;
              const onSelectIndex = onSelect(index);
              const active = activeIndex === index;
              const Flyout = FlyoutMenu;
              const MenuItem = DesktopMenuItem;

              return (
                <MenuItem
                  key={name}
                  label={name}
                  url={url}
                  active={active}
                  onClose={onClose}
                  onSelect={onSelectIndex}
                  isTouch={isTouch}
                >
                  {childItems?.length > 0 && (
                    <Flyout
                      contentId={contentId}
                      columns={childItems}
                      hierarchy={name}
                      onClose={onClose}
                      isTouch={isTouch}
                    />
                  )}
                </MenuItem>
              );
            })}
          </ul>
        </div>
      </div>
      <div className={overlayClassName} data-testid="desktop-menu-overlay" />
    </nav>
  );
};

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

DesktopMenu.propTypes = {
  child: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
};
